import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Slider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    padding: theme.spacing(2),
    maxWidth: 600,
    width: '100%'
  }
}));

const ColorChip = styled(Chip)(({ theme, selected }) => ({
  margin: theme.spacing(0.5),
  borderRadius: 16,
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  color: selected ? '#fff' : theme.palette.text.primary,
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
  }
}));

const COLORS = [
  'blue', 'red', 'green', 'grey', 'black', 'white',
  'brown', 'pink', 'purple', 'yellow', 'orange'
];

const STYLES = [
  'casual', 'formal', 'streetwear', 'vintage',
  'minimalist', 'bohemian', 'athleisure', 'preppy'
];

const OCCASIONS = [
  'everyday', 'work', 'party', 'date', 'workout',
  'special occasion', 'vacation', 'formal event'
];

export default function ShoppingIntentModal({ open, onClose, onSubmit }) {
  const [intent, setIntent] = useState({
    category: '',
    colors: [],
    style: '',
    occasion: '',
    max_price: 200
  });

  const handleSubmit = () => {
    onSubmit(intent);
    onClose();
  };

  const handleSkip = () => {
    onClose();
  };

  const handleColorToggle = (color) => {
    setIntent(prev => ({
      ...prev,
      colors: prev.colors.includes(color)
        ? prev.colors.filter(c => c !== color)
        : [...prev.colors, color]
    }));
  };

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" fontWeight={600}>
            What are you looking for?
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            Select colors (multiple)
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {COLORS.map((color) => (
              <ColorChip
                key={color}
                label={color}
                onClick={() => handleColorToggle(color)}
                selected={intent.colors.includes(color)}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            Choose your style
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {STYLES.map((style) => (
              <ColorChip
                key={style}
                label={style}
                onClick={() => setIntent({ ...intent, style })}
                selected={intent.style === style}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            What's the occasion?
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {OCCASIONS.map((occasion) => (
              <ColorChip
                key={occasion}
                label={occasion}
                onClick={() => setIntent({ ...intent, occasion })}
                selected={intent.occasion === occasion}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            Price Range
          </Typography>
          <Slider
            value={intent.max_price}
            onChange={(_, value) => setIntent({ ...intent, max_price: value })}
            min={0}
            max={500}
            step={10}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `$${value}`}
            sx={{ mt: 2 }}
          />
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">$0</Typography>
            <Typography variant="body2" color="text.secondary">$500</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleSkip}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Skip
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ borderRadius: 2 }}
        >
          Start Shopping
        </Button>
      </DialogActions>
    </StyledDialog>
  );
} 